import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

// third-party
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

// application
import Currency from "../shared/Currency";
import { Check100Svg } from "../../svg";

// data stubs
import theme from "../../data/theme";
import { clearCart } from "../../store/cart";
import { url } from "../../services/utils";
import shopApi from "../../api/shop";

export default function ShopPageOrderSuccess() {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const userId = user?.userConnected?.user?.id;
    const locale = useSelector((state) => state.locale);
    const [response, setResponse] = useState(null);

    useEffect(() => {
        shopApi.clearCart(userId).then(() => {
            dispatch(clearCart());
        });
        shopApi.getUserOrder(userId, locale).then((res) => {
            setResponse(res);
        });
    }, [dispatch, userId, locale]);

    if (!response) {
        return (
            <div className="order-success__header">
                <Check100Svg className="order-success__icon" />
                <h1 className="order-success__title"><FormattedMessage id="Shop.orderReceived" defaultMessage="Order Received" /></h1>
                <div className="order-success__subtitle"><FormattedMessage id="Shop.thankYou" defaultMessage="Thank you" /></div>
                <div className="order-success__actions">
                    <Link to="/" className="btn btn-xs btn-secondary">
                        <FormattedMessage id="Shop.goToHomepage" defaultMessage="Go To Homepage" />
                    </Link>
                </div>
            </div>
        );
    }

    const items = response.orderItems.map((item) => {
        const product = item.product;
        
        return (
            <tr key={item._id}>
                <td className="order-list__column-image">
                    <div className="product-image">
                        <Link to={url.product(product)} className="product-image__body">
                            <img className="product-image__img" src={product?.images[0]} alt={product?.name} />
                        </Link>
                    </div>
                </td>
                <td className="order-list__column-product">
                    <Link to={url.product(product)}>{product?.name}</Link>
                </td>
                <td className="order-list__column-quantity" data-title="Qty:">
                    {item.quantity}
                </td>
                <td className="order-list__column-total">
                    <Currency value={product.price * item.quantity} />
                </td>
            </tr>
        );
    });

    return (
        <div className="block order-success">
            <Helmet>
                <title>{`Order Success — ${theme.name}`}</title>
            </Helmet>

            <div className="container">
                <div className="order-success__body">
                    <div className="order-success__header">
                        <Check100Svg className="order-success__icon" />
                        <h1 className="order-success__title"><FormattedMessage id="Shop.orderReceived" defaultMessage="Order Received" /></h1>
                        <div className="order-success__subtitle"><FormattedMessage id="Shop.thankYou" defaultMessage="Thank you" /></div>
                        <div className="order-success__actions">
                            <Link to="/" className="btn btn-xs btn-secondary">
                                <FormattedMessage id="Shop.goToHomepage" defaultMessage="Go To Homepage" />
                            </Link>
                        </div>
                    </div>

                    <div className="order-success__meta">
                        <ul className="order-success__meta-list">
                            <li className="order-success__meta-item">
                                <span className="order-success__meta-title"><FormattedMessage id="Shop.orderNumber" defaultMessage="Order number:" /></span>
                                <span className="order-success__meta-value">{`#${response._id}`}</span>
                            </li>
                            <li className="order-success__meta-item">
                                <span className="order-success__meta-title"><FormattedMessage id="Shop.createdAt" defaultMessage="Created at:" /></span>
                                <span className="order-success__meta-value">
                                    {new Date(response.dateOrdered).toLocaleDateString()}
                                </span>
                            </li>
                            <li className="order-success__meta-item">
                                <span className="order-success__meta-title"><FormattedMessage id="Shop.total" defaultMessage="Total:" /></span>
                                <span className="order-success__meta-value">
                                    <Currency value={response.totalPrice} />
                                </span>
                            </li>
                            <li className="order-success__meta-item">
                                <span className="order-success__meta-title"><FormattedMessage id="Shop.paymentMethod" defaultMessage="Payment method:" /></span>
                                <span className="order-success__meta-value">Card</span>
                            </li>
                        </ul>
                    </div>

                    <div className="card">
                        <div className="order-list">
                            <table>
                                <thead className="order-list__header">
                                    <tr>
                                        <th className="order-list__column-label" colSpan="2">
                                            <FormattedMessage id="Shop.products" defaultMessage="Products" />
                                        </th>
                                        <th className="order-list__column-quantity">
                                            <FormattedMessage id="Cart.quantity" defaultMessage="Quantity" />
                                        </th>
                                        <th className="order-list__column-total">
                                            <FormattedMessage id="Cart.total" defaultMessage="Total" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="order-list__products">{items}</tbody>
                                <tfoot className="order-list__footer">
                                    <tr>
                                        <th className="order-list__column-label" colSpan="3">
                                            <FormattedMessage id="Cart.total" defaultMessage="Total" />
                                        </th>
                                        <td className="order-list__column-total">
                                            <Currency value={response.totalPrice} />
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    <div className="row mt-3 no-gutters mx-n2">
                        <div className="col-12 px-2">
                            <div className="card address-card">
                                <div className="address-card__body">
                                    <div className="address-card__badge address-card__badge--muted">
                                        <FormattedMessage id="Shop.shippingAddress" defaultMessage="Shipping Address" />
                                    </div>
                                    <div className="address-card__name">{response.shippingAddress1}</div>
                                    <div className="address-card__row">
                                        {response.country}
                                        <br />
                                        {`${response.zip}, ${response.city}`}
                                        <br />
                                        {response.shippingAddress1} {response.shippingAddress2}
                                    </div>
                                    <div className="address-card__row">
                                        <div className="address-card__row-title"><FormattedMessage id="Account.phone" defaultMessage="Phone Number" /></div>
                                        <div className="address-card__row-content">{response.phone}</div>
                                    </div>
                                    <div className="address-card__row">
                                        <div className="address-card__row-title"><FormattedMessage id="Account.email" defaultMessage="Email Address" /></div>
                                        <div className="address-card__row-content">{response.email}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <div className="col-sm-6 col-12 px-2 mt-sm-0 mt-3">
                            <div className="card address-card">
                                <div className="address-card__body">
                                    <div className="address-card__badge address-card__badge--muted">
                                        Billing Address
                                    </div>

                                    <div className="address-card__name">
                                        {response.shippingAddress1}
                                    </div>
                                    <div className="address-card__row">
                                        {response.country}
                                        <br />
                                        {`${response.zip}, ${response.city}`}
                                        <br />
                                        {response.shippingAddress1}
                                    </div>
                                    <div className="address-card__row">
                                        <div className="address-card__row-title">Phone Number</div>
                                        <div className="address-card__row-content">{response.phone}</div>
                                    </div>
                                    <div className="address-card__row">
                                        <div className="address-card__row-title">Email Address</div>
                                        <div className="address-card__row-content">{response.email}</div>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
